import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Ear, Flag, GitBranch, Mic, UserCheck, PhoneOff, FlagOff } from "lucide-react";
import { useDemoLive } from "../demo/demo-live-provider";
import { HoldButton } from "../controls/hold-button";
import { FlagControls } from "../flag-controls";
import { flagCall, unflagCall } from "../supervision-api";
import { getManualFlag, removeManualFlag, saveManualFlag } from "../lib/flags-store";
import type { ReviewFlag, SupervisedCall } from "../types";
import { toast } from "sonner";

function Turn({ turn, eventsBySeq }: { turn: any; eventsBySeq: Record<number, any[]> }) {
  const evs = eventsBySeq[turn.seq] || [];
  const flagged = turn.flags?.length > 0 || evs.length > 0;
  const why = evs.map((e) => `${e.rule_label} (+${e.score_delta})`).join("\n") || turn.flags?.join(", ");

  if (turn.flags?.includes("steer")) {
    return (
      <div className="my-1 px-2.5 py-1.5 border-l-2 border-primary bg-primary/10 text-[11px] font-mono text-primary rounded-r" data-testid={`steer-turn-${turn.seq}`}>
        SUPERVISOR STEER &rarr; {turn.text}
      </div>
    );
  }

  const speakerLabel = turn.speaker === "ai" ? "AI" : turn.speaker === "customer" ? "CUSTOMER" : "SUPERVISOR";
  const speakerColor = turn.speaker === "ai" ? "text-muted-foreground" : turn.speaker === "customer" ? "text-foreground font-semibold" : "text-primary font-bold";

  return (
    <div className="py-2 border-b border-border/20 last:border-b-0" data-testid={`turn-${turn.seq}`}>
      <div className="flex items-center gap-2">
        <span className={`text-[10px] font-mono ${speakerColor}`}>{speakerLabel}</span>
        {flagged && (
          <span className="text-[9px] font-mono text-risk-amber font-semibold px-1 rounded bg-risk-amber/10" title={why}>
            ⚑ {evs[0]?.rule_label || turn.flags?.join(" ")}
          </span>
        )}
        <span className="ml-auto text-[10px] font-mono tnum text-muted-foreground">
          {turn.ts ? new Date(turn.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ""}
        </span>
      </div>
      <div
        className={`text-[12px] leading-snug mt-1 ${
          turn.speaker === "ai" ? "text-muted-foreground" : "text-foreground"
        } ${flagged ? "underline decoration-dotted decoration-risk-amber underline-offset-2" : ""}`}
        title={flagged ? why : undefined}
      >
        {turn.text}
      </div>
      {turn.translation && (
        <div className="text-[10px] text-muted-foreground/75 italic leading-snug mt-0.5">{turn.translation}</div>
      )}
    </div>
  );
}

export function CallDrawerV2({ callId, onClose }: { callId: string | null; onClose: () => void }) {
  const { calls, intervene } = useDemoLive();
  const call = callId ? calls[callId] : null;

  const [mode, setMode] = useState<"steer" | "speak" | null>(null);
  const [input, setInput] = useState("");
  const [opening, setOpening] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [flag, setFlag] = useState<ReviewFlag | null>(() => (callId ? getManualFlag(callId) : null));

  useEffect(() => {
    if (!callId) {
      setFlag(null);
      return;
    }
    setFlag(getManualFlag(callId));
    const handleFlagsChanged = () => {
      setFlag(getManualFlag(callId));
    };
    window.addEventListener("supervision-flags-changed", handleFlagsChanged);
    return () => window.removeEventListener("supervision-flags-changed", handleFlagsChanged);
  }, [callId]);

  const handleToggleFlag = async () => {
    if (!callId || !call) return;
    try {
      if (flag) {
        await unflagCall(callId);
        removeManualFlag(callId);
        toast.info("Call unflagged");
      } else {
        const meta: Partial<SupervisedCall> = {
          call_id: callId,
          contact_name: call.customer_name,
          customer_phone: call.customer_ref,
          agent_name: call.campaign_name,
          campaign: call.campaign_name,
          created_at: call.started_at,
          lifecycle: "live",
        };
        await flagCall(callId, "");
        saveManualFlag(callId, "", meta);
        toast.success("Call flagged for review");
      }
      setFlag(getManualFlag(callId));
    } catch {
      toast.error("Failed to update call flag");
    }
  };

  useEffect(() => {
    if (!callId) return;
    setMode(null);
    setOpening(null);
    setInput("");
  }, [callId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [call?.turns]);

  const turns = useMemo(() => call?.turns || [], [call?.turns]);
  const events = useMemo(() => call?.events || [], [call?.events]);
  const interventions = useMemo(() => call?.interventions || [], [call?.interventions]);
  const context = useMemo(() => call?.context || {}, [call?.context]);

  const eventsBySeq = useMemo(() => {
    const map: Record<number, any[]> = {};
    for (const e of events) {
      if (e.evidence_turn_seq != null) {
        if (!map[e.evidence_turn_seq]) map[e.evidence_turn_seq] = [];
        map[e.evidence_turn_seq].push(e);
      }
    }
    return map;
  }, [events]);

  if (!callId || !call) return null;

  const act = async (type: string, text?: string, ruleId?: string) => {
    try {
      const res = await intervene(call.id, type, text, ruleId);
      if (type === "takeover" && res?.script) {
        setOpening(res.script);
      }
      toast.success(`Action applied: ${type}`);
    } catch {
      toast.error(`Failed to apply action: ${type}`);
    }
  };

  const submitGuidance = () => {
    if (!input.trim() || !mode) return;
    act(mode, input.trim());
    setInput("");
    setMode(null);
  };

  const dismissLatestFalsePos = () => {
    const active = events.find((e) => !e.false_positive);
    if (active) {
      act("false_positive", undefined, active.rule_id);
    } else {
      toast.info("No active risk events to dismiss");
    }
  };

  const objective = context.objective || `Collect FMT or capture promise-to-pay for ${call.campaign_name || "account"}`;
  const lastInteractions = context.last_interactions || [
    { ts: "2026-06-14", channel: "SMS", note: "Delivered" },
    { ts: "2026-06-09", channel: "CALL", note: "Connected" },
    { ts: "2026-06-02", channel: "EMAIL", note: "Opened" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="absolute inset-0 z-40 flex justify-end bg-black/25 backdrop-blur-[0.5px]"
      onClick={onClose}
    >
      <motion.div
        data-testid="call-drawer"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 320 }}
        onClick={(e) => e.stopPropagation()}
        className="w-[min(760px,65vw)] h-full border-l border-border bg-card flex flex-col shadow-2xl overflow-hidden select-none"
      >
        {/* 2-Pane Header */}
      <div className="h-10 px-3 border-b border-border flex items-center justify-between bg-muted/40 shrink-0 select-none">
        <div className="flex items-center gap-2.5 min-w-0 font-mono text-xs">
          <span
            className={`font-bold ${
              call.risk_tier === "red"
                ? "text-[#ef4444]"
                : call.risk_tier === "amber"
                ? "text-[#f59e0b]"
                : "text-muted-foreground"
            }`}
          >
            {call.risk_tier.toUpperCase()} {call.risk_score}
          </span>
          <span className="font-semibold text-foreground">{call.customer_ref}</span>
          <span className="text-muted-foreground truncate">
            {call.customer_name} · {call.campaign_name || "General"}
          </span>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-border bg-muted/30 text-foreground uppercase">
            {call.state.replace(/_/g, " ")}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <FlagControls
            callId={call.id}
            flag={flag}
            onChanged={() => setFlag(getManualFlag(call.id))}
          />
          <button
            onClick={onClose}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition"
            aria-label="Close drawer"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {/* Opening statement alert if taken over */}
      {opening && (
        <div className="p-2.5 bg-primary/10 border-b border-primary/20 text-xs font-mono text-primary flex flex-col gap-1">
          <span className="font-bold text-[10px] uppercase tracking-wider">Takeover Active — Read to caller:</span>
          <span className="italic leading-snug">"{opening}"</span>
        </div>
      )}

      {/* 2-PANE BODY */}
      <div className="flex-1 min-h-0 flex divide-x divide-border overflow-hidden">
        {/* PANE 1: LIVE TRANSCRIPT */}
        <div className="flex-1 min-w-0 flex flex-col h-full bg-card">
          <div className="px-3 py-1.5 border-b border-border/40 text-[9px] font-mono font-bold tracking-widest text-muted-foreground uppercase">
            LIVE TRANSCRIPT
          </div>
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto column-scroll px-3 py-2 divide-y divide-border/20">
            {turns.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs text-muted-foreground font-mono italic">
                Connecting audio stream…
              </div>
            ) : (
              turns.map((t) => <Turn key={t.seq} turn={t} eventsBySeq={eventsBySeq} />)
            )}
          </div>
        </div>

        {/* PANE 2: CUSTOMER & CONTEXT RAIL */}
        <div className="w-[260px] shrink-0 flex flex-col h-full bg-muted/15 overflow-y-auto hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden p-3 text-xs font-mono select-none divide-y divide-border/40">
          {/* Section: Customer Metadata */}
          <div className="pb-3">
            <div className="text-[9px] font-bold tracking-widest text-muted-foreground uppercase mb-2">
              CUSTOMER
            </div>
            <div className="space-y-1.5 text-[11px]">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">ID</span>
                <span className="font-semibold text-foreground">{call.customer_ref}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">ACCOUNT</span>
                <span className="text-foreground">{context.account_state || "overdue_7d"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">CONSENT</span>
                <span className="text-foreground">{context.consent_status || "recorded"}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">WINDOW</span>
                <span className={context.in_calling_window !== false ? "text-emerald-500 font-semibold" : "text-red-500 font-semibold"}>
                  {context.in_calling_window !== false ? "IN-WINDOW" : "OFF-WINDOW"}
                </span>
              </div>
            </div>
          </div>

          {/* Section: Objective */}
          <div className="py-3">
            <div className="text-[9px] font-bold tracking-widest text-muted-foreground uppercase mb-1.5">
              OBJECTIVE
            </div>
            <p className="text-[11px] leading-relaxed text-foreground">
              {objective}
            </p>
          </div>

          {/* Section: Last Interactions */}
          <div className="py-3">
            <div className="text-[9px] font-bold tracking-widest text-muted-foreground uppercase mb-1.5">
              LAST INTERACTIONS
            </div>
            <div className="space-y-1 text-[11px]">
              {lastInteractions.map((item: any, i: number) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-muted-foreground">{item.ts}</span>
                  <span className="text-foreground uppercase font-semibold">{item.channel || "CALL"}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Section: Risk Events */}
          <div className="py-3">
            <div className="text-[9px] font-bold tracking-widest text-muted-foreground uppercase mb-1.5 flex items-center justify-between">
              <span>RISK EVENTS</span>
              {events.length > 0 && <span className="text-risk-amber font-semibold">({events.length})</span>}
            </div>
            {events.length === 0 ? (
              <span className="text-muted-foreground text-[11px] italic">none fired</span>
            ) : (
              <div className="space-y-1.5">
                {events.map((e) => (
                  <div
                    key={e.id}
                    className={`p-1.5 rounded border text-[10px] leading-tight ${
                      e.false_positive
                        ? "line-through opacity-50 border-border bg-background"
                        : e.tier === "red"
                        ? "border-red-300/80 bg-red-50/80 dark:bg-red-950/20 text-[#ef4444]"
                        : "border-amber-300/80 bg-amber-50/80 dark:bg-amber-950/20 text-[#f59e0b]"
                    }`}
                  >
                    <div className="font-semibold flex items-center justify-between">
                      <span>{e.rule_label}</span>
                      {!e.false_positive && (
                        <button
                          onClick={() => act("false_positive", undefined, e.rule_id)}
                          className="hover:underline flex items-center gap-0.5 text-[8px] text-muted-foreground hover:text-foreground"
                          title="Mark as false positive"
                        >
                          <FlagOff size={9} /> False+
                        </button>
                      )}
                    </div>
                    <div className="text-muted-foreground text-[9px] mt-0.5">
                      {e.ts ? new Date(e.ts).toLocaleTimeString() : ""} · +{e.score_delta} · turn #{e.evidence_turn_seq}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section: Interventions */}
          <div className="pt-3">
            <div className="text-[9px] font-bold tracking-widest text-muted-foreground uppercase mb-1.5">
              INTERVENTIONS
            </div>
            {interventions.length === 0 ? (
              <span className="text-muted-foreground text-[11px] italic">none yet</span>
            ) : (
              <div className="space-y-1 text-[10px]">
                {interventions.map((inv: any, i: number) => (
                  <div key={i} className="text-foreground">
                    <span className="text-primary font-semibold">{inv.type}</span>: {inv.text || "applied"}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Steer/Speak input box */}
      {mode && (
        <div className="p-2 border-t border-border bg-muted/40 flex gap-2 shrink-0">
          <input
            autoFocus
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitGuidance()}
            placeholder={mode === "steer" ? "Guide AI (e.g. Offer 10% discount)..." : "Speak verbatim into call..."}
            className="flex-1 bg-background border border-border px-2.5 py-1 text-xs text-foreground placeholder:text-muted-foreground rounded outline-none focus:border-primary font-mono"
          />
          <button
            onClick={submitGuidance}
            className="px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded hover:opacity-90 font-mono"
          >
            Send
          </button>
          <button
            onClick={() => setMode(null)}
            className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground font-mono"
          >
            Cancel
          </button>
        </div>
      )}

      {/* BOTTOM ACTION TOOLBAR MATCHING SCREENSHOT */}
      <div className="p-2 border-t border-border bg-card flex items-center gap-1.5 shrink-0 text-xs font-mono select-none">
        <button
          onClick={() => act("listen")}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-2 border border-border bg-background hover:bg-muted text-foreground rounded font-semibold transition"
          title="Listen in to this call"
        >
          <Ear size={13} className="text-muted-foreground" />
          <span>LISTEN</span>
        </button>

        <button
          onClick={() => setMode("steer")}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-2 border border-border bg-background hover:bg-muted text-foreground rounded font-semibold transition"
          title="Guide the AI agent"
        >
          <GitBranch size={13} className="text-muted-foreground" />
          <span>STEER</span>
        </button>

        <button
          onClick={() => setMode("speak")}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-2 border border-border bg-background hover:bg-muted text-foreground rounded font-semibold transition"
          title="Speak into the call"
        >
          <Mic size={13} className="text-muted-foreground" />
          <span>SPEAK</span>
        </button>

        <HoldButton
          testId="takeover-button"
          onHold={() => void act("takeover")}
          className="px-4 py-2 text-[11px] font-mono font-bold bg-[#ea580c] hover:bg-[#c2410c] text-white rounded justify-center shrink-0"
        >
          <span className="tracking-wider">HOLD TO TAKE OVER</span>
        </HoldButton>

        <button
          onClick={() => act("terminate")}
          className="px-3 py-2 border border-border bg-background hover:bg-red-50 dark:hover:bg-red-950/30 text-foreground hover:text-destructive rounded font-semibold transition flex items-center gap-1"
          title="End this call immediately"
        >
          <PhoneOff size={13} className="text-muted-foreground" />
          <span>END</span>
        </button>

        <button
          onClick={dismissLatestFalsePos}
          className="px-3 py-2 border border-border bg-background hover:bg-muted text-foreground rounded font-semibold transition flex items-center gap-1"
          title="Mark current top rule trigger as false positive"
        >
          <FlagOff size={13} className="text-muted-foreground" />
          <span>FALSE POS</span>
        </button>

        <button
          type="button"
          onClick={handleToggleFlag}
          data-testid="flag-call-button"
          className={`px-3 py-2 border rounded font-semibold transition flex items-center gap-1 shrink-0 ${
            flag
              ? "border-amber-500/50 bg-amber-500/10 text-amber-500 dark:text-amber-400"
              : "border-border bg-background hover:bg-muted text-foreground"
          }`}
          title={flag ? "Unflag this call" : "Flag call manually for supervisor review"}
        >
          <Flag size={13} className={flag ? "fill-current text-amber-500" : "text-muted-foreground"} />
          <span>{flag ? "FLAGGED" : "FLAG"}</span>
        </button>
      </div>
    </motion.div>
  </motion.div>
  );
}
